import { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Button, Chip, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DirectDebitService } from '../../../../services/directDebitService';
import { SignWithUaePassButton } from '../../../../shared/components/signWithUaePassButton';
import { DirectDebitDdaStatus } from '../../../../constants/directDebitEnums';
import { mapDdaStatusFromApiCode, getDdaStatusColor } from '../../../../helpers/directDebitStatusHelpers';

const directDebitService = new DirectDebitService();

export const MandateCard = ({ loanId }) => {
	const { t } = useTranslation();
	const [mandate, setMandate] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadMandate();
	}, [loanId]);

	const loadMandate = async () => {
		try {
			setLoading(true);
			const response = await directDebitService.getMandateByLoanId(loanId);
			setMandate(response.data);
		} catch (error) {
			console.error('Error loading mandate:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleRefreshStatus = async () => {
		if (!mandate?.id) return;
		try {
			setLoading(true);
			const response = await directDebitService.getMandateStatus(mandate.id);
			if (response?.data) {
				setMandate((prev) => ({ ...prev, ...response.data }));
			}
		} catch (error) {
			console.error('Error refreshing mandate status:', error);
		} finally {
			setLoading(false);
		}
	};

	const isSignedStatus = () => {
		if (!mandate?.status) return !!mandate?.signedAt;
		const status = mapDdaStatusFromApiCode(mandate.status);
		return status === DirectDebitDdaStatus.PendingBankSubmission || 
		       status === DirectDebitDdaStatus.PendingBankApproval || 
		       status === DirectDebitDdaStatus.Active || 
		       !!mandate?.signedAt;
	};

	const handleViewPdf = async (signed = false) => {
		try {
			const response = signed
				? await directDebitService.getSignedPdf(mandate.id)
				: await directDebitService.getUnsignedPdf(mandate.id);
			
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			window.open(url, '_blank');
		} catch (error) {
			console.error('Error viewing PDF:', error);
		}
	};

	const getStatusColor = (status) => {
		return getDdaStatusColor(status);
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" p={3}>
				<CircularProgress />
			</Box>
		);
	}

	if (!mandate) {
		return null;
	}

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Typography variant="h6">
						{t('directDebitMandate')}
					</Typography>
					<Box display="flex" alignItems="center" gap={1}>
						<Chip
							label={mandate.statusDescription || mandate.status}
							color={getStatusColor(mandate.status)}
							size="small"
						/>
					</Box>
				</Box>

				<Box mb={2}>
					<Typography variant="body2" color="textSecondary">
						{t('Bank')}: {mandate.customerAccountBankName}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{t('Payment Method')}: {mandate.paymentMethod}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{t('Valid From')}: {new Date(mandate.commencesOn).toLocaleDateString()}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{t('Valid Until')}: {new Date(mandate.expiresOn).toLocaleDateString()}
					</Typography>
				</Box>

				<Box display="flex" gap={1} flexWrap="wrap">
					{mapDdaStatusFromApiCode(mandate.status) === DirectDebitDdaStatus.Pending && (
						<SignWithUaePassButton mandateId={mandate.id} size="small" />
					)}
					
					<Button
						variant="outlined"
						onClick={() => handleViewPdf(isSignedStatus())}
						size="small"
					>
						{t('View Mandate')}
					</Button>
					
					<Button
							variant="outlined"
							size="small"
							disabled={loading}
							onClick={handleRefreshStatus}
							sx={{ flexShrink: 0 }}
						>
							{t('refreshStatus')}
						</Button>
				</Box>
			</CardContent>
		</Card>
	);
};
